import React, { useState, useEffect } from "react";
import baseSway from "../../../media/base-sway-unscreen.gif";
import celebration from "../../../media/celebration-unscreen.gif";
import {
    isSlotCorrectInContext,
    isBlankBoxCorrectInContext,
    getSlotLiveStatus,
    getBlankBoxLiveStatus,
} from "../../../utils/quizResultsUtils";
import './SingleQuestionPage.css';

function SingleQuestionPage({ quizQuestion, onAnswerSelect, ButtonsAreClickable, onInputChange }) {
    const [selectedAnswer, setSelectedAnswer] = useState("select");
    const [FreeTextInputValue, setFreeTextInputValue] = useState("");
    const [currentVideoSource, setCurrentVideoSource] = useState(baseSway);
    const [correctFeedback, setCorrectFeedback] = useState(false);
    const [incorrectFeedback, setIncorrectFeedback] = useState(false);
    const [buttonsAreClickable, setButtonsAreClickable] = useState(true);
    const [blankBoxAnswers, setBlankBoxAnswers] = useState({});
    const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
    const [droppedLabels, setDroppedLabels] = useState({});
    const [shuffledLabels, setShuffledLabels] = useState([]);
    const [nextBoxId, setNextBoxId] = useState(null);
    const [freeTextError, setFreeTextError] = useState(false);
    const [blankBoxError, setBlankBoxError] = useState(false);
    const [mixedSlotAnswers, setMixedSlotAnswers] = useState({});
    const [mixedError, setMixedError] = useState(false);
    const [nextMixedDragSlotId, setNextMixedDragSlotId] = useState(null);

    // Reset all state and initialise drag labels when the question changes.
    // The two effects that were previously here have been merged to avoid
    // running two separate passes over quizQuestion on every question change.
    useEffect(() => {
        setSelectedAnswer("select");
        setFreeTextInputValue("");
        setCurrentVideoSource(baseSway);
        setCorrectFeedback(false);
        setIncorrectFeedback(false);
        setButtonsAreClickable(true);
        setFreeTextError(false);
        setBlankBoxError(false);
        setMixedSlotAnswers({});
        setMixedError(false);

        if (quizQuestion.questionType === 'Drag-And-Drop-Question') {
            const labels = quizQuestion.dragBoxes.map(box => box.label);
            setShuffledLabels([...labels].sort(() => Math.random() - 0.5));
            const sorted = quizQuestion.dragBoxes.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const firstBox = sorted[0];
            if (firstBox) setNextBoxId(firstBox.id);
        } else if (quizQuestion.questionType === 'Mixed') {
            const pool = Array.isArray(quizQuestion.dragPoolLabels) && quizQuestion.dragPoolLabels.length > 0
                ? quizQuestion.dragPoolLabels
                : (quizQuestion.slots || []).filter(s => s.slotType === 'drag-and-drop').map(s => s.label).filter(Boolean);
            setShuffledLabels([...pool].sort(() => Math.random() - 0.5));
            const firstDrag = (quizQuestion.slots || [])
                .filter(s => s.slotType === 'drag-and-drop')
                .sort((a, b) => (a.order || 0) - (b.order || 0))[0];
            setNextMixedDragSlotId(firstDrag?.id ?? null);
        }
    }, [quizQuestion]);

    const handleButtonClick = (answer) => {
        setSelectedAnswer(answer.id);
        
        // Send the input update to the tutor
        onInputChange?.({
            questionType: 'Multiple-Choice-Question',
            value: answer
        });

        const isCorrect = answer.id === quizQuestion.correctAnswer;
        
        if (isCorrect) {
            setCorrectFeedback(true);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, answer.id);
        if (isCorrect) {
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        }
        setButtonsAreClickable(false);
    };

    const handleFreeTextSubmit = (inputValue) => {
        // Check if answer is empty or just whitespace
        if (!FreeTextInputValue.trim()) {
            setFreeTextError(true);
            return; // Stop execution if answer is empty
        }
        
        // Clear any previous error
        setFreeTextError(false);

        const lowerCaseTexts = quizQuestion.answers.map(answer => answer.text.toLowerCase().trim());
        const isCorrect = lowerCaseTexts.includes(FreeTextInputValue.toLowerCase().trim());
        
        // Send the input update to the tutor
        onInputChange?.({
            questionType: 'Free-Text-Question',
            value: inputValue,
            isSubmitted: true,
            isCorrect: isCorrect
        });

        if (isCorrect) {
            setCorrectFeedback(true);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, FreeTextInputValue);
        if (isCorrect) {
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        }
        setButtonsAreClickable(false);
    };

    // Add input tracking for free text as user types
    const handleFreeTextChange = (e) => {
        const value = e.target.value;
        setFreeTextInputValue(value);
        
        onInputChange?.({
            questionType: 'Free-Text-Question',
            value: value
        });
    };

    const handleDropDownSubmit = () => {


        if (!quizQuestion.answers.some(answer => answer.id === selectedAnswer)) {
            return;
        }

        const isCorrect = selectedAnswer === quizQuestion.correctAnswer;

        onInputChange?.({
            questionType: 'Drop-Down-List',
            value: selectedAnswer,
            isSubmitted: true,
            isCorrect: isCorrect
        });

        if (isCorrect) {
            setCorrectFeedback(true);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, selectedAnswer);
        if (isCorrect) {
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        }
        setButtonsAreClickable(false);
    };

    const ButtonColour = (selectedAnswer, answer) => {
        let Formatting = 'whitespace-nowrap h-12 px-2 my-2 text-lg hover:bg-nova-light-green text-nova-purple border border-2 border-nova-purple rounded-lg focus:outline-none';

        let AdditionalFormatting = ''

        if (selectedAnswer === answer.id && answer.id === quizQuestion.correctAnswer) {
            AdditionalFormatting += ' bg-nova-green';
        } else if (selectedAnswer === answer.id && answer.id !== quizQuestion.correctAnswer) {
            AdditionalFormatting += ' bg-nova-coral text-white';
        } else {
            AdditionalFormatting += ' bg-nova-cream';
        }

        Formatting += AdditionalFormatting;

        return Formatting;
    };

    const handleBlankBoxInput = (boxId, value) => {
        const updatedAnswers = {
            ...blankBoxAnswers,
            [boxId]: value
        };
        setBlankBoxAnswers(updatedAnswers);

        // Send the input update to the tutor
        onInputChange?.({
            questionType: 'Blank-Box-Question',
            value: updatedAnswers
        });
    };

    const handleBlankBoxKeyPress = (e, currentBox) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const nextBox = quizQuestion.blankBoxes.find(box => box.order === currentBox.order + 1);
            if (nextBox) {
                setCurrentBoxIndex(currentBox.order);
                document.getElementById(`box-${nextBox.id}`).focus();
            } else {
                handleBlankBoxSubmit();
            }
        }
    };

    const handleBlankBoxSubmit = () => {
        // Check if any box is empty
        const anyEmptyBox = quizQuestion.blankBoxes.some(box => 
            !blankBoxAnswers[box.id] || blankBoxAnswers[box.id].trim() === ''
        );
        
        if (anyEmptyBox) {
            setBlankBoxError(true);
            return; // Stop execution if any box is empty
        }
        
        // Clear any previous error
        setBlankBoxError(false);

        // Check if all answers are correct, similar to QuestionPage.js
        const allAnswers = quizQuestion.blankBoxes.map(box => ({
            boxId: box.id,
            userAnswer: blankBoxAnswers[box.id] || '',
            correctAnswer: box.answer
        }));

        // Link-group aware correctness for blank boxes.
        const isCorrect = quizQuestion.blankBoxes.every(box => 
            isBlankBoxCorrectInContext(box, quizQuestion.blankBoxes, blankBoxAnswers)
        );

        // Send the input update with isSubmitted flag
        onInputChange?.({
            questionType: 'Blank-Box-Question',
            value: blankBoxAnswers,
            isSubmitted: true,
            isCorrect: isCorrect
        });

        if (isCorrect) {
            setCorrectFeedback(true);
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, allAnswers);
        setButtonsAreClickable(false);
    };

    const removeOneLabel = (labels, target) => {
        const idx = labels.indexOf(target);
        if (idx === -1) return labels;
        return [...labels.slice(0, idx), ...labels.slice(idx + 1)];
    };

    const handleLabelClick = (label) => {
        if (!buttonsAreClickable) return;
        
        // Find the next empty box by order
        const filledBoxIds = Object.keys(droppedLabels);
        const nextBox = quizQuestion.dragBoxes
            .filter(box => !filledBoxIds.includes(box.id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];

        if (nextBox) {
            const updatedLabels = {
                ...droppedLabels,
                [nextBox.id]: label
            };
            
            // Update droppedLabels with the new label
            setDroppedLabels(updatedLabels);

            // Send regular update without isSubmitted flag
            onInputChange?.({
                questionType: 'Drag-And-Drop-Question',
                value: updatedLabels
            });

            // Update shuffledLabels
            setShuffledLabels(prev => removeOneLabel(prev, label));

            // Set next box to highlight
            const nextEmptyBox = quizQuestion.dragBoxes
                .filter(box => !filledBoxIds.includes(box.id) && box.id !== nextBox.id)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
            setNextBoxId(nextEmptyBox?.id || null);
        }
    };

    const handleDragBoxSubmit = () => {
        const isCorrect = quizQuestion.dragBoxes.every(box => 
            droppedLabels[box.id] === box.label
        );
        
        // Send the input update with isSubmitted flag
        onInputChange?.({
            questionType: 'Drag-And-Drop-Question',
            value: droppedLabels,
            isSubmitted: true,
            isCorrect: isCorrect
        });
        
        if (isCorrect) {
            setCorrectFeedback(true);
            setCurrentVideoSource(celebration);
            setTimeout(() => {
                setCurrentVideoSource(baseSway);
            }, 1500);
        } else {
            setIncorrectFeedback(true);
        }
        
        onAnswerSelect(isCorrect, droppedLabels);
        setButtonsAreClickable(false);
    };

    // Mixed question handlers
    const slots = (quizQuestion.slots || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const mixedDragSlots = slots.filter(s => s.slotType === 'drag-and-drop');
    const hasPosition = (s) => s.x != null && s.y != null && s.width != null && s.height != null;

    const handleMixedSlotChange = (slotId, value) => {
        setMixedSlotAnswers(prev => ({ ...prev, [slotId]: value }));
        onInputChange?.({ questionType: 'Mixed', value: { ...mixedSlotAnswers, [slotId]: value } });
    };

    const handleMixedLabelClick = (label) => {
        if (!buttonsAreClickable || !nextMixedDragSlotId) return;
        const filledIds = Object.keys(mixedSlotAnswers).filter(id => {
            const slot = slots.find(s => s.id === id);
            return slot?.slotType === 'drag-and-drop';
        });
        const nextSlot = mixedDragSlots.find(s => s.id === nextMixedDragSlotId) || mixedDragSlots.find(s => !filledIds.includes(s.id));
        if (!nextSlot) return;
        const updated = { ...mixedSlotAnswers, [nextSlot.id]: label };
        setMixedSlotAnswers(updated);
        setShuffledLabels(prev => removeOneLabel(prev, label));
        const after = mixedDragSlots.filter(s => s.id !== nextSlot.id && !filledIds.includes(s.id)).sort((a, b) => (a.order || 0) - (b.order || 0))[0];
        setNextMixedDragSlotId(after?.id ?? null);
        onInputChange?.({ questionType: 'Mixed', value: updated });
    };

    const handleMixedReset = () => {
        const pool = Array.isArray(quizQuestion.dragPoolLabels) && quizQuestion.dragPoolLabels.length > 0
            ? quizQuestion.dragPoolLabels
            : (quizQuestion.slots || []).filter(s => s.slotType === 'drag-and-drop').map(s => s.label).filter(Boolean);
        setShuffledLabels([...pool].sort(() => Math.random() - 0.5));
        const withoutDrag = { ...mixedSlotAnswers };
        mixedDragSlots.forEach(s => delete withoutDrag[s.id]);
        setMixedSlotAnswers(withoutDrag);
        const first = mixedDragSlots[0];
        setNextMixedDragSlotId(first?.id ?? null);
    };

    const handleMixedSubmit = () => {
        const allSlots = slots.filter(hasPosition);
        const missing = allSlots.some(s => {
            const v = mixedSlotAnswers[s.id];
            return v === undefined || v === null || String(v).trim() === '';
        });
        if (missing) {
            setMixedError(true);
            return;
        }
        setMixedError(false);

        const isCorrect = allSlots.every(slot =>
            isSlotCorrectInContext(slot, allSlots, mixedSlotAnswers)
        );

        onInputChange?.({ questionType: 'Mixed', value: mixedSlotAnswers, isSubmitted: true, isCorrect });

        if (isCorrect) {
            setCorrectFeedback(true);
            setCurrentVideoSource(celebration);
            setTimeout(() => setCurrentVideoSource(baseSway), 1500);
        } else {
            setIncorrectFeedback(true);
        }

        onAnswerSelect(isCorrect, mixedSlotAnswers);
        setButtonsAreClickable(false);
    };

    // Add handleReset function
    const handleReset = () => {
        // Reset droppedLabels to empty
        setDroppedLabels({});
        // Re-shuffle and restore all labels
        const labels = quizQuestion.dragBoxes.map(box => box.label);
        setShuffledLabels([...labels].sort(() => Math.random() - 0.5));
        // Reset nextBoxId to the first box by order
        const sorted = quizQuestion.dragBoxes.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const firstBox = sorted[0];
        if (firstBox) {
            setNextBoxId(firstBox.id);
        }
    };

    return (                  
                <div className="flex flex-col items-center  h-full w-full">
                    {/* Only show this div if there's an image */}
                    {quizQuestion.image && quizQuestion.image.trim() !== '' ? (
                        // removed border border-red-500 from div below as no longer testing
                        <div className="w-full text-center text-2xl font-bold">
                            <p>{quizQuestion.question}</p>
                        </div>
                    ) : null}

                    {/* This div will either contain the image or the question text */}
                    {quizQuestion.questionType !== 'Blank-Box-Question' && quizQuestion.questionType !== 'Drag-And-Drop-Question' && quizQuestion.questionType !== 'Mixed'
                      && (
                        <div className=" w-full h-[52vh] flex justify-center items-center">
                            {quizQuestion.image && quizQuestion.image.trim() !== '' ? (
                                <img src={quizQuestion.image} alt="Question" className="max-w-full max-h-full object-contain" />
                            ) : (
                                <p className="single-question-page-title text-2xl font-bold text-center px-4">{quizQuestion.question}</p>
                            )}
                        </div>
                    )}

                    {correctFeedback && (
                        <div className="w-full text-center border-2 border-green-600 rounded-lg correct-feedback">
                            <p>Correct!</p>
                            {quizQuestion.correctFeedback && <p>{quizQuestion.correctFeedback}</p>}
                        </div>
                    )}      
                                 
                    {incorrectFeedback && (
                        <div className="w-full text-center border-2 border-red-600 rounded-lg incorrect-feedback">
                            <p>Incorrect!</p>
                            {quizQuestion.incorrectFeedback && <p>{quizQuestion.incorrectFeedback}</p>}
                        </div>
                    )}

                  {quizQuestion.questionType === 'Multiple-Choice-Question' ? (
                        <div className="w-full flex flex-col gap-3">
                            {quizQuestion.answers.map((answer) => (
                                <button
                                    key={answer.id}
                                    type="button"
                                    onClick={buttonsAreClickable ? () => handleButtonClick(answer) : undefined}
                                    className={`quiz-option-card ${selectedAnswer === answer.id ? 'quiz-option-selected' : ''} ${selectedAnswer === answer.id && answer.id === quizQuestion.correctAnswer ? '!border-nova-green !bg-nova-light-green' : ''} ${selectedAnswer === answer.id && answer.id !== quizQuestion.correctAnswer ? '!border-nova-coral' : ''}`}
                                >
                                    <span className="quiz-option-dot">
                                        {selectedAnswer === answer.id && <span className="quiz-option-dot-inner" />}
                                    </span>
                                    <span className="quiz-option-label">{answer.text}</span>
                                </button>
                            ))}
                        </div>
                    ) : quizQuestion.questionType === 'Drop-Down-List' ? (
                        <main className="drop-down-container w-full">
                            <div className="quiz-select-wrapper w-full my-2">
                                <select
                                    value={selectedAnswer}
                                    onChange={(e) => {
                                        setSelectedAnswer(e.target.value);
                                        onInputChange?.({
                                            questionType: 'Drop-Down-List',
                                            value: e.target.value
                                        });
                                    }}
                                    className={`quiz-select quiz-focus-ring w-full ${correctFeedback ? 'quiz-input-correct' : ''} ${incorrectFeedback ? 'quiz-input-incorrect' : ''}`}
                                >
                                    <option value="select">Select Answer</option>
                                    {quizQuestion.answers.map((answer) => (
                                        <option key={answer.id} value={answer.id}>
                                            {answer.text}
                                        </option>
                                    ))}
                                </select>
                                <span className="quiz-select-chevron" aria-hidden>▾</span>
                            </div>
                            <button
                                type="button"
                                onClick={buttonsAreClickable ? handleDropDownSubmit : undefined}
                                className="quiz-submit-btn mt-4 focus:outline-none"
                            >
                                Submit
                            </button>
                        </main>
                    ) : quizQuestion.questionType === 'Free-Text-Question' ? (
                        <div className="w-full flex flex-col items-center justify-center my-2">
                            <input
                                type="text"
                                value={FreeTextInputValue}
                                onChange={handleFreeTextChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.repeat && buttonsAreClickable) handleFreeTextSubmit(FreeTextInputValue);
                                }}
                                placeholder="Enter answer here"
                                className={`quiz-text-input quiz-focus-ring w-full max-w-md ${correctFeedback ? 'quiz-input-correct' : ''} ${incorrectFeedback ? 'quiz-input-incorrect quiz-input-shake' : ''} ${freeTextError ? '!border-nova-coral' : ''}`}
                            />
                            {freeTextError && (
                                <div className="w-full text-center text-nova-coral font-medium mt-2 text-sm">
                                    Please enter an answer before submitting.
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={buttonsAreClickable ? () => handleFreeTextSubmit(FreeTextInputValue) : undefined}
                                className="quiz-submit-btn w-32 mt-4 focus:outline-none"
                            >
                                Submit
                            </button>
                        </div>
                    ) : quizQuestion.questionType === 'Blank-Box-Question' ? (
                        <div className="relative flex justify-center items-center">
                            <img 
                                src={quizQuestion.image} 
                                alt="Question" 
                                className="object-contain max-h-[60vh] max-w-full"
                                draggable="false"
                            />
                            {quizQuestion.blankBoxes.map((box) => (
                                <div
                                    key={box.id}
                                    className="absolute max-md:min-h-[44px] max-md:min-w-[44px]"
                                    style={{
                                        left: `${box.x}%`,
                                        top: `${box.y}%`,
                                        width: `${box.width}%`,
                                        height: `${box.height}%`
                                    }}
                                >
                                    <input
                                        id={`box-${box.id}`}
                                        type="text"
                                        value={blankBoxAnswers[box.id] || ''}
                                        onChange={(e) => handleBlankBoxInput(box.id, e.target.value)}
                                        onKeyPress={(e) => handleBlankBoxKeyPress(e, box)}
                                        autoComplete="off"
                                        className={`w-full h-full p-1 border-2 max-md:min-h-[44px] max-md:text-base ${
                                            (() => {
                                                const liveStatus = getBlankBoxLiveStatus(box, quizQuestion.blankBoxes, blankBoxAnswers);
                                                if (liveStatus === 'green') return 'border-green-500 bg-green-100';
                                                if (liveStatus === 'red') return 'border-red-500 bg-red-100';
                                                return blankBoxError ? 'border-orange-500 bg-orange-200' : 'border-black bg-white';
                                            })()
                                        }`}
                                        disabled={!buttonsAreClickable}
                                    />
                                </div>
                            ))}
                            {/*blankBoxError && (
                                <div className="absolute bottom-0 w-full text-center text-red-500 bg-white bg-opacity-80 p-1">
                                    Please fill in all blank boxes before submitting.
                                </div>
                            )*/}
                        </div>
                    ) : quizQuestion.questionType === 'Drag-And-Drop-Question' ? (
                        //removed border border-orange-500 from div below as no longer testing

                                <div className="relative flex justify-center items-center">
                                    <img 
                                        src={quizQuestion.image} 
                                        alt="Question" 
                                        className="object-contain max-h-[60vh] max-w-full"
                                        draggable="false"
                                    />
                                    {quizQuestion.dragBoxes.map((box) => (
                                        <div
                                            key={box.id}
                                            className={`absolute single-question-drag-slot quiz-drop-zone ${droppedLabels[box.id] ? 'quiz-drop-zone-filled' : ''} ${
                                                !buttonsAreClickable
                                                    ? droppedLabels[box.id] === box.label
                                                        ? '!border-nova-green !bg-nova-light-green'
                                                        : '!border-nova-coral'
                                                    : box.id === nextBoxId && !droppedLabels[box.id]
                                                    ? '!border-nova-green !bg-nova-light-green'
                                                    : ''
                                            }`}
                                            style={{
                                                left: `${box.x}%`,
                                                top: `${box.y}%`,
                                                width: `${box.width}%`,
                                                height: `${box.height}%`
                                            }}
                                        >
                                            {droppedLabels[box.id] ? (
                                                <span className="quiz-drop-zone-label">{droppedLabels[box.id]}</span>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                    ) : quizQuestion.questionType === 'Mixed' ? (
                        <div className="flex flex-col items-center">
                            {(!quizQuestion.image || !quizQuestion.image.trim()) && (
                                <div className="w-full text-center text-2xl font-bold mb-2">
                                    <p>{quizQuestion.question}</p>
                                </div>
                            )}
                            <div className="relative" style={{ display: 'inline-block', maxWidth: '100%' }}>
                                <img
                                    src={quizQuestion.image}
                                    alt="Question"
                                    className="object-contain max-h-[60vh] max-w-full"
                                    draggable="false"
                                />
                                {slots.filter(hasPosition).map((slot) => (
                                    <div
                                        key={slot.id}
                                        className="absolute"
                                        style={{
                                            left: `${slot.x}%`,
                                            top: `${slot.y}%`,
                                            width: `${slot.width}%`,
                                            height: `${slot.height}%`
                                        }}
                                    >
                                        {slot.slotType === 'blank-box' && (
                                            <input
                                                type="text"
                                                value={mixedSlotAnswers[slot.id] || ''}
                                                onChange={(e) => handleMixedSlotChange(slot.id, e.target.value)}
                                                disabled={!buttonsAreClickable}
                                                className={`w-full h-full p-1 border-2 text-sm max-md:text-base ${
                                                    (() => {
                                                        const liveStatus = getSlotLiveStatus(slot, slots, mixedSlotAnswers);
                                                        if (liveStatus === 'green') return 'border-green-500 bg-green-100';
                                                        if (liveStatus === 'red') return 'border-red-500 bg-red-100';
                                                        return mixedError ? 'border-orange-500 bg-orange-200' : 'border-gray-600 bg-white';
                                                    })()
                                                }`}
                                            />
                                        )}
                                        {slot.slotType === 'drag-and-drop' && (
                                            <div
                                                className={`w-full h-full flex items-center justify-center quiz-drop-zone ${mixedSlotAnswers[slot.id] ? 'quiz-drop-zone-filled' : ''} ${
                                                    !buttonsAreClickable && mixedSlotAnswers[slot.id]
                                                        ? mixedSlotAnswers[slot.id] === (slot.label || '')
                                                            ? '!border-nova-green !bg-nova-light-green'
                                                            : '!border-nova-coral'
                                                        : slot.id === nextMixedDragSlotId && !mixedSlotAnswers[slot.id]
                                                        ? '!border-nova-green !bg-nova-light-green'
                                                        : ''
                                                }`}
                                            >
                                                {mixedSlotAnswers[slot.id] ? (
                                                    <span className="quiz-drop-zone-label text-sm">{mixedSlotAnswers[slot.id]}</span>
                                                ) : null}
                                            </div>
                                        )}
                                        {slot.slotType === 'multiple-choice' && (
                                            <div className="w-full h-full overflow-auto p-1 flex flex-col gap-1">
                                                {(slot.options || []).map((opt) => (
                                                    <button
                                                        key={opt.id}
                                                        type="button"
                                                        onClick={() => buttonsAreClickable && handleMixedSlotChange(slot.id, opt.id)}
                                                        disabled={!buttonsAreClickable}
                                                        className={`quiz-option-card !min-h-9 py-1 ${(mixedSlotAnswers[slot.id] || '') === opt.id ? 'quiz-option-selected' : ''}`}
                                                    >
                                                        <span className="quiz-option-dot !w-4 !h-4">
                                                            {(mixedSlotAnswers[slot.id] || '') === opt.id && <span className="quiz-option-dot-inner !w-1.5 !h-1.5" />}
                                                        </span>
                                                        <span className="quiz-option-label text-sm">{opt.text}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {slot.slotType === 'dropdown' && (() => {
                                            const liveStatus = getSlotLiveStatus(slot, slots, mixedSlotAnswers);
                                            return (
                                                <select
                                                    value={mixedSlotAnswers[slot.id] || ''}
                                                    onChange={(e) => handleMixedSlotChange(slot.id, e.target.value)}
                                                    disabled={!buttonsAreClickable}
                                                    className={`w-full h-full border-2 text-xs p-1 max-md:text-base ${
                                                        liveStatus === 'green'
                                                            ? 'border-green-500 bg-green-100'
                                                            : liveStatus === 'red'
                                                                ? 'border-red-500 bg-red-100'
                                                                : mixedError
                                                                    ? 'border-orange-500 bg-orange-200'
                                                                    : 'border-gray-400'
                                                    }`}
                                                >
                                                    <option value="">Select...</option>
                                                    {(slot.options || []).map((opt) => (
                                                        <option key={opt.id} value={opt.id}>{opt.text}</option>
                                                    ))}
                                                </select>
                                            );
                                        })()}
                                    </div>
                                ))}
                            </div>
                            {mixedDragSlots.length > 0 && (
                                <div className="flex flex-wrap justify-center gap-2 mt-2">
                                    {shuffledLabels.map((label, idx) => (
                                        <div
                                            key={idx}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleMixedLabelClick(label)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleMixedLabelClick(label); }}
                                            className={`quiz-chip text-sm ${Object.values(mixedSlotAnswers).some(v => v === label) ? 'quiz-chip-used' : ''}`}
                                        >
                                            {label}
                                        </div>
                                    ))}
                                    {buttonsAreClickable && Object.keys(mixedSlotAnswers).some(id => mixedDragSlots.some(s => s.id === id)) && (
                                        <button type="button" onClick={handleMixedReset} className="quiz-secondary-btn !py-2 text-sm">Reset labels</button>
                                    )}
                                </div>
                            )}
                            <div className="mt-2">
                                {mixedError && <p className="text-nova-coral font-medium text-sm text-center">Fill in all fields before submitting.</p>}
                                {buttonsAreClickable && (
                                    <button
                                        type="button"
                                        onClick={handleMixedSubmit}
                                        className="quiz-submit-btn focus:outline-none"
                                    >
                                        Submit
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {quizQuestion.questionType === 'Drag-And-Drop-Question' && (
                        <div className="flex max-md:flex-col max-md:items-center max-md:gap-3">
                            <div className="mt-2 flex flex-wrap justify-center gap-2">
                                {shuffledLabels.map((label, index) => (
                                    <div
                                        key={index}
                                        className={`quiz-chip ${Object.values(droppedLabels).includes(label) ? 'quiz-chip-used' : ''}`}
                                        onClick={() => handleLabelClick(label)}
                                    >
                                        {label}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2 flex mx-2">
                                {buttonsAreClickable && Object.keys(droppedLabels).length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="quiz-secondary-btn focus:outline-none"
                                    >
                                        Reset Labels
                                    </button>
                                )}
                                {buttonsAreClickable && shuffledLabels.length === 0 && (
                                    <button
                                        type="button"
                                        onClick={handleDragBoxSubmit}
                                        className="quiz-submit-btn focus:outline-none"
                                    >
                                        Submit
                                    </button>
                                )}

                            </div>
                        </div>
                   )}
                    {quizQuestion.questionType === 'Blank-Box-Question' && buttonsAreClickable && (
                        <div className="flex flex-col items-center">

                            {blankBoxError && (
                                <div className="w-full text-center text-red-500 mt-1">
                                    Please fill in all blank boxes before submitting.
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={handleBlankBoxSubmit}
                                className="quiz-submit-btn focus:outline-none"
                            >
                                Submit
                            </button>

                        </div>
                    )}
                </div>
        
    );
}

export default SingleQuestionPage;